import * as THREE from 'three';
import type { HudFrame } from '../hud/hud.js';

const RADAR_SIZE_PX = 160;
const RADAR_MARGIN_PX = 24;
const RADAR_DISC_RADIUS = 0.88;
const RADAR_HEIGHT_SCALE = 0.55;
const CONTACT_POOL = 64;

type RadarContact = {
  readonly sprite: THREE.Sprite;
  readonly ring: THREE.Sprite;
  readonly stalk: THREE.Line<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  readonly stalkPos: Float32Array;
};

/** A DOM frame in CSS px (top-left origin) — a structural subset of DOMRect, so
 *  `element.getBoundingClientRect()` is assignable directly. */
export interface RadarRect {
  readonly left: number;
  readonly bottom: number;
  readonly width: number;
  readonly height: number;
}

/** A WebGL viewport/scissor box in physical px (bottom-left origin). */
export interface RadarViewport {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * Convert the radar's DOM frame (CSS px, top-left origin) into a WebGL
 * viewport/scissor box (physical px, bottom-left origin), so the 3D disc ALWAYS
 * lands exactly inside the DOM border in any mode/position — including cockpit
 * mode where the frame moves to bottom-centre. Multiplies by the device pixel
 * ratio and flips Y. Falls back to the historic bottom-right box when no frame
 * is supplied (radar hidden, or queried before its first layout).
 */
export function radarViewport(
  rect: RadarRect | null | undefined,
  dpr: number,
  viewportW: number,
  viewportH: number,
): RadarViewport {
  if (!rect || rect.width <= 0 || rect.height <= 0) {
    const size = RADAR_SIZE_PX * dpr;
    const margin = RADAR_MARGIN_PX * dpr;
    return { x: viewportW * dpr - size - margin, y: margin, w: size, h: size };
  }
  return {
    x: rect.left * dpr,
    y: (viewportH - rect.bottom) * dpr,
    w: rect.width * dpr,
    h: rect.height * dpr,
  };
}

const clamp = (v: number, min: number, max: number): number => Math.max(min, Math.min(max, v));

const clampDisc = (x: number, z: number): { x: number; z: number } => {
  const len = Math.hypot(x, z);
  if (len <= 1 || len === 0) return { x, z };
  return { x: x / len, z: z / len };
};

const circlePoints = (radius: number, segments = 48): THREE.Vector3[] => {
  const pts: THREE.Vector3[] = [];
  for (let i = 0; i <= segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    pts.push(new THREE.Vector3(Math.cos(a) * radius, 0, Math.sin(a) * radius));
  }
  return pts;
};

const createDiscFill = (): THREE.Mesh => {
  const disc = new THREE.Mesh(
    new THREE.CircleGeometry(RADAR_DISC_RADIUS, 48),
    new THREE.MeshBasicMaterial({ color: 0x0b1f26, transparent: true, opacity: 0.5, toneMapped: false }),
  );
  disc.rotation.x = -Math.PI / 2;
  return disc;
};

const createRing = (radius: number, color: number, opacity: number): THREE.LineLoop =>
  new THREE.LineLoop(
    new THREE.BufferGeometry().setFromPoints(circlePoints(radius)),
    new THREE.LineBasicMaterial({ color, transparent: true, opacity, toneMapped: false }),
  );

const createChevron = (): THREE.Line => {
  const pts = [
    new THREE.Vector3(-0.12, 0, -0.34),
    new THREE.Vector3(0, 0, -0.58),
    new THREE.Vector3(0.12, 0, -0.34),
  ];
  return new THREE.Line(
    new THREE.BufferGeometry().setFromPoints(pts),
    new THREE.LineBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, toneMapped: false }),
  );
};

const createSpriteTexture = (ring = false): THREE.CanvasTexture => {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) return new THREE.CanvasTexture(canvas);
  ctx.clearRect(0, 0, size, size);
  ctx.strokeStyle = 'rgba(255,255,255,0.95)';
  ctx.fillStyle = 'rgba(255,255,255,0.95)';
  ctx.lineWidth = 6;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, ring ? 20 : 10, 0, Math.PI * 2);
  if (ring) ctx.stroke();
  else ctx.fill();
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;
  return texture;
};

const contactColor = (blip: HudFrame['radarBlips'][number]): number => {
  if (blip.kind === 'hostile') return 0xff4444;
  if (blip.kind === 'neutral') return 0x3fd0e0;
  if (blip.kind === 'body') return 0x5a6b78;
  return (blip.threat ?? 0) >= 3 ? 0xff4444 : 0xffb000;
};

const createContact = (dotMap: THREE.Texture, ringMap: THREE.Texture): RadarContact => {
  const sprite = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: dotMap, transparent: true, depthWrite: false, toneMapped: false }),
  );
  const ring = new THREE.Sprite(
    new THREE.SpriteMaterial({ map: ringMap, transparent: true, depthWrite: false, toneMapped: false }),
  );
  const stalkPos = new Float32Array(6);
  const stalk = new THREE.Line(
    new THREE.BufferGeometry().setAttribute('position', new THREE.BufferAttribute(stalkPos, 3)),
    new THREE.LineBasicMaterial({ transparent: true, opacity: 0.5, toneMapped: false }),
  );
  sprite.visible = false;
  ring.visible = false;
  stalk.visible = false;
  return { sprite, ring, stalk, stalkPos };
};

export class Radar3D {
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.1, 10);
  private readonly contacts: RadarContact[] = [];

  constructor() {
    this.camera.position.set(0, 1.35, 1.85);
    this.camera.lookAt(0, 0, 0);
    this.scene.add(createDiscFill(), createRing(RADAR_DISC_RADIUS, 0x3fd0e0, 0.8));
    this.scene.add(createRing(RADAR_DISC_RADIUS * 0.66, 0x3fd0e0, 0.22));
    this.scene.add(createRing(RADAR_DISC_RADIUS * 0.33, 0x3fd0e0, 0.18));
    this.scene.add(createChevron());
    this.createContacts();
  }

  private createContacts(): void {
    const dotMap = createSpriteTexture(false);
    const ringMap = createSpriteTexture(true);
    for (let i = 0; i < CONTACT_POOL; i++) {
      const contact = createContact(dotMap, ringMap);
      this.contacts.push(contact);
      this.scene.add(contact.stalk, contact.sprite, contact.ring);
    }
  }

  update(blips: HudFrame['radarBlips']): void {
    for (let i = 0; i < this.contacts.length; i++) this.updateContact(this.contacts[i]!, blips[i]);
  }

  private updateContact(contact: RadarContact, blip?: HudFrame['radarBlips'][number]): void {
    if (!blip) {
      contact.sprite.visible = false;
      contact.ring.visible = false;
      contact.stalk.visible = false;
      return;
    }
    const flat = clampDisc(clamp(blip.x, -1, 1), clamp(blip.z, -1, 1));
    const y = clamp(blip.y, -1, 1) * RADAR_HEIGHT_SCALE;
    const px = flat.x * RADAR_DISC_RADIUS;
    const pz = flat.z * RADAR_DISC_RADIUS;
    const color = contactColor(blip);
    contact.sprite.material.color.setHex(color);
    contact.sprite.position.set(px, y, pz);
    contact.sprite.scale.setScalar(blip.kind === 'body' ? 0.11 : 0.14);
    contact.sprite.visible = true;
    contact.ring.material.color.setHex(0xffffff);
    contact.ring.position.copy(contact.sprite.position);
    contact.ring.scale.setScalar(0.24);
    contact.ring.visible = blip.selected;
    contact.stalk.material.color.setHex(color);
    contact.stalkPos[0] = px;
    contact.stalkPos[1] = 0;
    contact.stalkPos[2] = pz;
    contact.stalkPos[3] = px;
    contact.stalkPos[4] = y;
    contact.stalkPos[5] = pz;
    (contact.stalk.geometry.getAttribute('position') as THREE.BufferAttribute).needsUpdate = true;
    contact.stalk.visible = Math.abs(y) > 0.02;
  }

  /** Render the disc into the scissored box derived from the radar's DOM frame
   *  (`rect`), so it tracks the DOM border wherever the layout places it. */
  render(renderer: THREE.WebGLRenderer, rect?: RadarRect | null): void {
    const dpr = renderer.getPixelRatio();
    const vp = radarViewport(rect, dpr, window.innerWidth, window.innerHeight);
    // three.js re-applies the pixel ratio inside setViewport/setScissor, so hand
    // it CSS px (vp is physical px → divide the ratio back out) to avoid double-
    // scaling. The math cancels at dpr=1; it only matters on HiDPI displays.
    const x = vp.x / dpr;
    const y = vp.y / dpr;
    const w = vp.w / dpr;
    const h = vp.h / dpr;
    renderer.clearDepth();
    renderer.setScissorTest(true);
    renderer.setViewport(x, y, w, h);
    renderer.setScissor(x, y, w, h);
    renderer.render(this.scene, this.camera);
    renderer.setScissorTest(false);
    renderer.setViewport(0, 0, window.innerWidth, window.innerHeight);
  }
}
